import {ASSETS_HISTORY_QUERY_KEY} from '@domain/assets'
import {CommentsCategory} from '@domain/comments'
import {
  MaterialStorageHistoryQueryKey,
  usePrefetchMaterialStorageHistory
} from '@domain/material-storage'
import {SCHEDULE_QUERY_KEY} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantContext} from '@hconnect/upm'
import {Box} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {useCommentsPermissions} from '@widgets/planning-comments'
import {
  PlanningChartScheduleDataProvider,
  PlanningChartStartEndProvider
} from '@widgets/planning-schedule-chart'
import {Moment} from 'moment'
import React from 'react'
import {useInterval} from 'react-use'

import {ONE_MINUTE_REFRESH_INTERVAL} from '../consts'
import {PlannerFeature, usePlannerFeature} from '../hooks'
import type {NumberRange} from '../utils'

import {OperatorViewChartContainer} from './OperatorViewChartContainer'

interface LayoutControlOperationProps {
  updateSliderDefaultRange: (range: number) => void
  sliderDefaultRange: number
  sliderMinMaxRange: NumberRange
  startOfOperatorView: Moment
  endOfOperatorView: Moment
}

export const LayoutControlOperation: React.FC<LayoutControlOperationProps> = ({
  updateSliderDefaultRange,
  sliderDefaultRange,
  sliderMinMaxRange: [sliderMinRange, sliderMaxRange],
  startOfOperatorView,
  endOfOperatorView
}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const queryClient = useQueryClient()
  const areCommentsEnabled = usePlannerFeature(PlannerFeature.PlanningComments)
  const {canViewComments: canViewScheduleItemComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )
  const {canViewComments: canViewAssetComments} = useCommentsPermissions(CommentsCategory.Assets)

  // prefetching queries
  usePrefetchMaterialStorageHistory(
    plantCode,
    startOfOperatorView.toISOString(),
    endOfOperatorView.toISOString()
  )

  const shouldRefreshAssetComments = areCommentsEnabled && canViewAssetComments
  const shouldRefreshScheduleItemsComments = areCommentsEnabled && canViewScheduleItemComments

  useInterval(() => {
    const start = startOfOperatorView.toISOString()
    const end = endOfOperatorView.toISOString()
    void queryClient.invalidateQueries({
      queryKey: [SCHEDULE_QUERY_KEY, {plantCode, start, end}],
      refetchType: 'all'
    })
    void queryClient.invalidateQueries({
      queryKey: [ASSETS_HISTORY_QUERY_KEY, plantCode, start, end],
      refetchType: 'all'
    })
    void queryClient.invalidateQueries({
      queryKey: [MaterialStorageHistoryQueryKey, plantCode, start, end],
      refetchType: 'all'
    })
    if (shouldRefreshScheduleItemsComments) {
      void queryClient.invalidateQueries({
        queryKey: ['comments'],
        refetchType: 'all'
      })
    }
    if (shouldRefreshAssetComments) {
      void queryClient.invalidateQueries({
        queryKey: ['comments'],
        refetchType: 'all'
      })
    }
  }, ONE_MINUTE_REFRESH_INTERVAL * 3)

  return (
    <Box {...dataTestId('layout_control_operator_view')}>
      <PlanningChartStartEndProvider
        updateSliderDefaultRange={updateSliderDefaultRange}
        sliderDefaultRange={sliderDefaultRange}
        sliderMinMaxRange={[sliderMinRange, sliderMaxRange]}
        startOfPlan={startOfOperatorView}
        endOfPlan={endOfOperatorView}
        boundary={[startOfOperatorView, endOfOperatorView]}
      >
        <PlanningChartScheduleDataProvider>
          <OperatorViewChartContainer plantCode={plantCode} />
        </PlanningChartScheduleDataProvider>
      </PlanningChartStartEndProvider>
    </Box>
  )
}
